/**
 * Canvas renderer for ProImager scene blobs.
 * Mirrors the export pipeline in HybridProImager so previews
 * (and therefore banner crops) are pixel-accurate.
 */

// ─── Sprite geometry ──────────────────────────────────────────────────────────

const clampSpriteScale = (value) => {
    const n = Number(value);
    return n === 2 || n === 4 ? n : 1;
};

const normaliseSpriteCrop = (crop, width, height) => {
    const w = Math.max(1, Math.round(Number(width) || 0));
    const h = Math.max(1, Math.round(Number(height) || 0));
    const left   = Math.max(0, Math.min(w - 1, Math.round(Number(crop?.left)   || 0)));
    const top    = Math.max(0, Math.min(h - 1, Math.round(Number(crop?.top)    || 0)));
    const right  = Math.max(0, Math.min(Math.max(0, w - left - 1), Math.round(Number(crop?.right)  || 0)));
    const bottom = Math.max(0, Math.min(Math.max(0, h - top  - 1), Math.round(Number(crop?.bottom) || 0)));
    return { left, top, right, bottom };
};

const getSpriteFrameMetrics = (sourceSize, scale = 1, crop = null) => {
    const width  = Math.max(0, Math.round(Number(sourceSize?.w) || 0));
    const height = Math.max(0, Math.round(Number(sourceSize?.h) || 0));
    const safeScale = clampSpriteScale(scale);
    if (!width || !height) {
        return { sourceW: width, sourceH: height, scale: safeScale,
            crop: { left: 0, top: 0, right: 0, bottom: 0 },
            frameW: 0, frameH: 0, drawW: 0, drawH: 0 };
    }
    const safeCrop = normaliseSpriteCrop(crop, width, height);
    const drawW = Math.max(1, width  - safeCrop.left - safeCrop.right);
    const drawH = Math.max(1, height - safeCrop.top  - safeCrop.bottom);
    return {
        sourceW: width, sourceH: height, scale: safeScale, crop: safeCrop,
        frameW: drawW * safeScale, frameH: drawH * safeScale,
        drawW, drawH,
    };
};

// ─── Text measurement ─────────────────────────────────────────────────────────

let _measureCtx = null;
const measureW = (s, font) => {
    if (!_measureCtx) _measureCtx = document.createElement('canvas').getContext('2d');
    _measureCtx.font = font;
    return Math.ceil(_measureCtx.measureText(String(s || '')).width || 0);
};

const getTextBoxMetrics = (t) => {
    const fontPx = Math.max(8, Math.round(Number(t?.fontSize) || 24));
    const weight  = t?.fontWeight === 'bold' ? 'bold'   : 'normal';
    const italic  = t?.italic               ? 'italic'  : 'normal';
    const font    = `${italic} ${weight} ${fontPx}px ${t?.fontFamily || 'sans-serif'}`;
    const stroke  = Math.max(0, Number(t?.strokeWidth) || 0);
    const text    = String(t?.text || '');
    const w = Math.max(24, measureW(text || ' ', font) + stroke * 2 + 8);
    const h = Math.max(16, Math.ceil(fontPx + stroke * 2 + 8));
    return { w, h, font, fontPx, stroke };
};

// ─── Room tile grid ───────────────────────────────────────────────────────────

const buildRoomTileGrid = (size, cfg = {}) => {
    const canvasW = Math.max(1, Math.round(Number(size?.w) || 0));
    const canvasH = Math.max(1, Math.round(Number(size?.h) || 0));
    const tileW   = Math.max(1, Math.round(Number(cfg.tileW)   || 64));
    const tileH   = Math.max(1, Math.round(Number(cfg.tileH)   || 39));
    const rowStepY = Math.max(1, Math.round(Number(cfg.rowStepY) || 16));
    const startYRatio = Number.isFinite(Number(cfg.startYRatio)) ? Number(cfg.startYRatio) : 0.35;
    const offsetX = Math.round(Number(cfg.offsetX) || 0);
    const offsetY = Math.round(Number(cfg.offsetY) || 0);

    const originX  = Math.round(canvasW / 2 - tileW / 2) + offsetX;
    const originY  = Math.round(canvasH * startYRatio)    + offsetY;
    const staggerX = Math.round(tileW / 2);

    const cols     = Math.ceil((canvasW + tileW * 4) / tileW);
    const rowsUp   = Math.ceil((originY + tileH * 2) / rowStepY);
    const rowsDown = Math.ceil((canvasH - originY + tileH * 2) / rowStepY);

    const out = [];
    for (let gy = -rowsUp; gy <= rowsDown; gy++) {
        const y = originY + gy * rowStepY;
        const rowShiftX = Math.abs(gy % 2) ? staggerX : 0;
        for (let gx = -cols; gx <= cols; gx++) {
            const x = originX + gx * tileW - rowShiftX;
            if (x > canvasW + tileW || x + tileW < -tileW || y > canvasH + tileH || y + tileH < -tileH) continue;
            out.push({ key: `${gx}:${gy}`, gx, gy, x, y, w: tileW, h: tileH });
        }
    }
    return out;
};

// ─── Outline effect ───────────────────────────────────────────────────────────

const applyOutlineEffect = (imgEl, color, tolerance = 0) => {
    const w = imgEl.naturalWidth || imgEl.width || 0;
    const h = imgEl.naturalHeight || imgEl.height || 0;
    if (!w || !h) return null;
    const hex = /^#[0-9a-fA-F]{6}$/.test(color) ? color : '#000000';
    const r2 = parseInt(hex.slice(1, 3), 16);
    const g2 = parseInt(hex.slice(3, 5), 16);
    const b2 = parseInt(hex.slice(5, 7), 16);
    const t  = Math.max(0, Math.min(255, tolerance));
    const canvas = document.createElement('canvas');
    canvas.width = w; canvas.height = h;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(imgEl, 0, 0);
    const imageData = ctx.getImageData(0, 0, w, h);
    const d = imageData.data;
    for (let i = 0; i < d.length; i += 4) {
        if (d[i + 3] === 0) continue;
        if (d[i] <= t && d[i + 1] <= t && d[i + 2] <= t) {
            d[i] = r2; d[i + 1] = g2; d[i + 2] = b2;
        } else {
            d[i + 3] = 0;
        }
    }
    ctx.putImageData(imageData, 0, 0);
    return canvas;
};

// ─── Water effect ────────────────────────────────────────────────────────────

const applyWaterEffect = (imgEl, color, heightPercent = 20, kind = 'habbo', curveMode = 'adaptive') => {
    const w = imgEl.naturalWidth || imgEl.width || 0;
    const h = imgEl.naturalHeight || imgEl.height || 0;
    if (!w || !h) return null;
    const hex = /^#[0-9a-fA-F]{6}$/.test(color) ? color : '#0a6e78';
    const wr = parseInt(hex.slice(1, 3), 16);
    const wg = parseInt(hex.slice(3, 5), 16);
    const wb = parseInt(hex.slice(5, 7), 16);
    const rr = Math.round(wr + (255 - wr) * 0.45);
    const rg = Math.round(wg + (255 - wg) * 0.45);
    const rb = Math.round(wb + (255 - wb) * 0.45);
    const canvas = document.createElement('canvas');
    canvas.width = w; canvas.height = h;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(imgEl, 0, 0);
    const imageData = ctx.getImageData(0, 0, w, h);
    const d = imageData.data;
    const base = Math.max(0, Math.min(100, heightPercent)) / 100;
    const useFurniAdaptive = kind === 'furni' && curveMode !== 'parabola';
    const waterTopRows = new Int32Array(w);
    const surfaceY = new Int32Array(w).fill(-1);
    if (useFurniAdaptive) {
        const cx = (w - 1) / 2;
        const maxLift = w * Math.tan(Math.PI / 6) / 4;
        const t = h > 0 ? Math.max(0, Math.min(1, (w / h - 0.5) / 1.5)) : 0;
        for (let x = 0; x < w; x++) {
            const nx = cx > 0 ? Math.abs(x - cx) / cx : 0;
            const liftFrac = nx * nx * (1 - t) + nx * t;
            waterTopRows[x] = Math.max(0, Math.round(h * (1 - base)) - Math.round(maxLift * liftFrac));
            for (let y = waterTopRows[x]; y < h; y++) {
                if (d[(y * w + x) * 4 + 3] > 0) { surfaceY[x] = y; break; }
            }
        }
    } else {
        const maxLift = Math.max(2, h * 0.10 * Math.min(1, h / w));
        for (let x = 0; x < w; x++) {
            const nx = w > 1 ? (2 * x / (w - 1)) - 1 : 0;
            waterTopRows[x] = Math.max(0, Math.round(h * (1 - base)) - Math.round(maxLift * nx * nx));
            for (let y = waterTopRows[x]; y < h; y++) {
                if (d[(y * w + x) * 4 + 3] > 0) { surfaceY[x] = y; break; }
            }
        }
    }
    for (let x = 0; x < w; x++) {
        if (surfaceY[x] === -1) continue;
        for (let y = waterTopRows[x]; y < h; y++) {
            const idx = (y * w + x) * 4;
            if (d[idx + 3] === 0) continue;
            if (y === surfaceY[x]) {
                d[idx] = rr; d[idx + 1] = rg; d[idx + 2] = rb;
            } else {
                const oR = d[idx], oG = d[idx + 1], oB = d[idx + 2];
                d[idx]     = Math.round(oR * 0.32 + wr * 0.68);
                d[idx + 1] = Math.round(oG * 0.32 + wg * 0.68);
                d[idx + 2] = Math.round(oB * 0.32 + wb * 0.68);
            }
        }
    }
    ctx.putImageData(imageData, 0, 0);
    return canvas;
};

// ─── Image loading ────────────────────────────────────────────────────────────

const loadImg = (src) => new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload  = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = src;
});

// ─── Sprite drawing ───────────────────────────────────────────────────────────

const drawSpriteOntoCtx = (ctx, img, op, W, H) => {
    const metrics = getSpriteFrameMetrics(
        { w: img.naturalWidth || img.width || 0, h: img.naturalHeight || img.height || 0 },
        op.scale || 1,
        op.crop  || null
    );
    if (!metrics.drawW || !metrics.drawH) return;

    const sc = metrics.scale;
    const dx = Math.round(W / 2 + op.x - metrics.sourceW * sc / 2 + metrics.crop.left * sc);
    const dy = Math.round(H / 2 + op.y - metrics.sourceH * sc / 2 + metrics.crop.top  * sc);
    // Round the centre so rotation/flip transforms never pivot on a sub-pixel boundary.
    const cx = Math.round(dx + metrics.frameW / 2);
    const cy = Math.round(dy + metrics.frameH / 2);
    const hdx = Math.round(-metrics.frameW / 2);
    const hdy = Math.round(-metrics.frameH / 2);

    ctx.save();
    ctx.translate(cx, cy);
    if (op.rotation) ctx.rotate(op.rotation * Math.PI / 180);
    if (op.flipX)    ctx.scale(-1,  1);
    if (op.flipY)    ctx.scale( 1, -1);
    ctx.drawImage(
        img,
        metrics.crop.left, metrics.crop.top, metrics.drawW, metrics.drawH,
        hdx, hdy, metrics.frameW, metrics.frameH
    );
    ctx.restore();
};

// ─── Background config ────────────────────────────────────────────────────────

const buildBgConfig = (publicUrl) => {
    const p = String(publicUrl || '').replace(/\/$/, '');
    return {
        default:   { type: 'flat' },
        grid:      { type: 'tile', url: `${p}/img/red_tile.webp`,  size: { w: 64, h: 32 } },
        grey_grid: { type: 'tile', url: `${p}/img/grey_tile.png`,  size: { w: 64, h: 32 } },
        room_tile: { type: 'room_tile', url: `${p}/img/roomTile.png`, tileW: 64, tileH: 41, rowStepY: 16, startYRatio: 0.35, offsetX: 0, offsetY: 0 },
        proimager: {
            type: 'layered',
            layers: {
                bg:   `${p}/img/proimager/chicka_bg.png`,
                bg_1: `${p}/img/proimager/chicka_bg_1.png`,
                bg_2: `${p}/img/proimager/chicka_bg_2.png`,
                bg_3: `${p}/img/proimager/chicka_bg_3.png`,
            },
        },
        fishingtv: {
            type: 'layered',
            layers: {
                bg:   `${p}/img/proimager/osiris_bg.png`,
                bg_1: `${p}/img/proimager/osiris_bg_1.png`,
                bg_2: `${p}/img/proimager/osiris_bg_2.png`,
                bg_3: `${p}/img/proimager/osiris_bg_3.png`,
            },
        },
        caddy: {
            type: 'layered',
            layers: {
                bg:   `${p}/img/proimager/caddy_bg.png`,
                bg_1: `${p}/img/proimager/caddy_bg_1.png`,
            },
        },
        sauna: {
            type: 'layered',
            layers: {
                bg:   `${p}/img/proimager/sauna_bg.png`,
                bg_1: `${p}/img/proimager/sauna_bg_1.png`,
            },
        },
        studio: {
            type: 'layered',
            layers: {
                bg:   `${p}/img/proimager/franklystudios_bg.png`,
                bg_1: `${p}/img/proimager/franklystudios_bg_1.png`,
                bg_2: `${p}/img/proimager/franklystudios_bg_2.png`,
                bg_3: `${p}/img/proimager/franklystudios_bg_3.png`,
            },
        },
        space: {
            type: 'layered',
            layers: {
                bg:   `${p}/img/proimager/space_bg.png`,
                bg_1: `${p}/img/proimager/space_bg_1.png`,
            },
        },
    };
};

// ─── Layer helpers ────────────────────────────────────────────────────────────

const getLayerKindAndId = (layerId) => {
    const s = String(layerId || '');
    const i = s.indexOf(':');
    if (i === -1) return { kind: '', id: '' };
    return { kind: s.slice(0, i), id: s.slice(i + 1) };
};

const getSceneLayerSeed = (scene) => {
    const bgKey = scene?.previewBg || 'default';
    const byBg  = scene?.layersStateByBg?.[bgKey];
    if (Array.isArray(byBg)             && byBg.length)             return byBg;
    if (Array.isArray(scene?.layersState) && scene.layersState.length) return scene.layersState;
    if (Array.isArray(scene?.layers)    && scene.layers.length)     return scene.layers;
    return [];
};

// ─── Main render ──────────────────────────────────────────────────────────────

/**
 * Renders a ProImager scene blob to an offscreen HTMLCanvasElement.
 *
 * @param {object} scene        - scene object from the imager payload
 * @param {object} opts
 * @param {function} opts.proxyUrl    - (url: string) => string  — proxies external image URLs
 * @param {string}   opts.publicUrl   - process.env.PUBLIC_URL
 * @param {object}   opts.speechParts - { frnt, mid, end } absolute URLs for default speech bubble parts
 * @returns {Promise<HTMLCanvasElement>}
 */
export async function renderSceneToCanvas(scene, { proxyUrl, publicUrl, speechParts }) {
    const W = Math.max(1, Number(scene?.imageWidth)  || 650);
    const H = Math.max(1, Number(scene?.imageHeight) || 500);

    await document.fonts.ready;

    const canvas = document.createElement('canvas');
    canvas.width  = W;
    canvas.height = H;
    const ctx = canvas.getContext('2d');
    ctx.imageSmoothingEnabled = false;

    const bgKey = scene?.previewBg || 'default';
    const bgConfig = buildBgConfig(publicUrl);
    const bg = bgConfig[bgKey] || bgConfig.default;
    const transparent = scene?.showTransparency || false;
    const gridTint = /^#[0-9a-fA-F]{6}$/.test(String(scene?.gridTint || '').trim())
        ? scene.gridTint.trim() : null;

    if (bg.type === 'flat' && !transparent) {
        const whiteTint = /^#[0-9a-fA-F]{6}$/.test(String(scene?.whiteBgTint || '').trim()) ? scene.whiteBgTint.trim() : null;
        ctx.fillStyle = whiteTint || '#f5f5f5';
        ctx.fillRect(0, 0, W, H);
    }
    // custom_bg URL is not persisted in the blob — render neutral grey
    if (bgKey === 'custom_bg' && !transparent) {
        ctx.fillStyle = '#888';
        ctx.fillRect(0, 0, W, H);
    }

    if (bg.type === 'room_tile') {
        const rawColor = String(scene?.roomTileBgColor || '').trim();
        const bgColor  = (() => {
            if (!/^#[0-9a-fA-F]{6}$/.test(rawColor)) return '#010101';
            const r = Math.max(0x01, parseInt(rawColor.slice(1, 3), 16));
            const g = Math.max(0x01, parseInt(rawColor.slice(3, 5), 16));
            const b = Math.max(0x01, parseInt(rawColor.slice(5, 7), 16));
            return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
        })();
        ctx.fillStyle = bgColor;
        ctx.fillRect(0, 0, W, H);
        const bgLayerVis = {
            ...(scene?.bgLayerVis && typeof scene.bgLayerVis === 'object' ? scene.bgLayerVis : {}),
            ...(scene?.roomTileCellVis && typeof scene.roomTileCellVis === 'object' ? scene.roomTileCellVis : {}),
        };
        const tileImg = await loadImg(bg.url);
        if (tileImg) {
            const tiles = buildRoomTileGrid({ w: W, h: H }, bg);
            for (const t of tiles) {
                if (bgLayerVis[`rt:${t.gx},${t.gy}`] === false) continue;
                ctx.drawImage(tileImg, t.x, t.y, t.w, t.h);
            }
        }
    }

    const tileMode   = !!scene?.tileMode;
    const zoom       = Number(scene?.zoom || 1) || 1;
    const cameraPos  = scene?.cameraPos || { x: 0, y: 0 };
    const layers     = getSceneLayerSeed(scene);
    const layerZ     = new Map(layers.map((l, i) => [String(l?.id || ''), i + 1]));

    const furniById  = new Map((scene?.furniSprites  || []).map(sp => [String(sp.id || ''), sp]));
    const habboById  = new Map((scene?.sceneSprites  || []).map(sp => [String(sp.id || ''), sp]));
    const imgById    = new Map((scene?.customImages  || []).map(im => [String(im.id || ''), im]));
    const textById   = new Map((scene?.textSprites   || []).map(t  => [String(t.id  || ''), t]));

    const drawOps = [];

    for (const l of layers) {
        if (!l || l.visible === false) continue;
        const { kind, id } = getLayerKindAndId(l.id);
        if (kind === 'speech') continue;
        const z = layerZ.get(String(l.id || '')) || 0;
        const layerOpacity = typeof l.opacity === 'number' ? l.opacity : 100;

        if (kind === 'moodlight') {
            const color = /^#[0-9a-fA-F]{6}$/.test((scene?.moodlightColor || '').trim())
                ? scene.moodlightColor.trim() : '#7c3aed';
            const opacity = Number.isFinite(scene?.moodlightOpacity) ? scene.moodlightOpacity : 0.5;
            drawOps.push({ z, type: 'moodlight', color, opacity });

        } else if (kind === 'bg') {
            if (bg.type === 'tile' && id === bgKey) {
                const _tileW = bg.size.w * zoom;
                const _tileOffX = (( Math.round(W / 2 - _tileW / 2)) % _tileW + _tileW) % _tileW;
                drawOps.push({ z, type: 'tile', url: bg.url,
                    tileW: _tileW, tileH: bg.size.h * zoom, tileOffX: _tileOffX });
            } else if (bg.type === 'layered' && bg.layers?.[id]) {
                drawOps.push({ z, type: 'centered', url: bg.layers[id], layerOpacity });
            }

        } else if (kind === 'furni') {
            const sp = furniById.get(id);
            if (sp) {
                const code = String(sp.code || '').trim().replace(/%2a/gi, '*');
                const d  = Number.isFinite(sp.rotI)  ? Number(sp.rotI)  : 0;
                const s  = Number.isFinite(sp.stateI) ? Number(sp.stateI) : 0;
                const fr = Number.isFinite(sp.frameI) ? Number(sp.frameI) : 0;
                const hexSuffix = sp.hex ? `&hex=${encodeURIComponent(String(sp.hex))}` : '';
                const raw = code
                    ? `https://furni.franklyorigins.net/api/furniture/render?furni=${encodeURIComponent(code)}&direction=${d}&state=${s}&frame=${fr}&width=120&height=120${hexSuffix}`
                    : String(sp.url || sp.baseUrl || '');
                const url = raw ? (proxyUrl ? proxyUrl(raw) : raw) : '';
                const outlineEffect = l.effects?.outline?.enabled ? l.effects.outline : null;
                const waterEffect   = l.effects?.water?.enabled   ? l.effects.water   : null;
                if (url) drawOps.push({
                    z, type: 'sprite', url, spriteKind: 'furni',
                    x: sp.pos?.x || 0, y: sp.pos?.y || 0,
                    flipX: !!sp.flipped, flipY: !!sp.flipY,
                    rotation: sp.rotation || 0,
                    scale: clampSpriteScale(sp.scale),
                    crop: sp.crop || null,
                    outlineEffect, waterEffect, layerOpacity,
                });
            }

        } else if (kind === 'habbo') {
            const sp = habboById.get(id);
            if (sp) {
                const snap = sp.snap || {};
                const base = String(snap.thumbUrl || '');
                if (base) {
                    const thumbUrl = base + (base.includes('?') ? '&' : '?') + 'crop=true';
                    const url = proxyUrl ? proxyUrl(thumbUrl) : thumbUrl;
                    const outlineEffect = l.effects?.outline?.enabled ? l.effects.outline : null;
                    const waterEffect   = l.effects?.water?.enabled   ? l.effects.water   : null;
                    drawOps.push({
                        z, type: 'sprite', url, spriteKind: 'habbo',
                        x: sp.pos?.x || 0, y: sp.pos?.y || 0,
                        flipX: !!snap.flipX, flipY: !!snap.flipY,
                        rotation: snap.rotation || 0,
                        scale: clampSpriteScale(snap.scale),
                        crop: snap.crop || null,
                        outlineEffect, waterEffect, layerOpacity,
                    });
                }
            }

        } else if (kind === 'custom_image') {
            const im = imgById.get(id);
            const outlineEffect = l.effects?.outline?.enabled ? l.effects.outline : null;
            const waterEffect   = l.effects?.water?.enabled   ? l.effects.water   : null;
            if (im && im.url) drawOps.push({
                z, type: 'sprite', url: im.url, spriteKind: 'custom_image',
                x: im.pos?.x || 0, y: im.pos?.y || 0,
                flipX: !!im.flipX, flipY: !!im.flipY,
                rotation: im.rotation || 0,
                scale: clampSpriteScale(im.scale),
                crop: im.crop || null,
                outlineEffect, waterEffect, layerOpacity,
            });

        } else if (kind === 'text') {
            const t = textById.get(id);
            if (t) drawOps.push({
                z, type: 'text',
                text: String(t.text || ''),
                x: t.pos?.x || 0, y: t.pos?.y || 0,
                rotation: t.rotation || 0,
                flipX: !!t.flipX, flipY: !!t.flipY,
                scale: clampSpriteScale(t.scale),
                fontFamily: t.fontFamily || 'sans-serif',
                fontSize: Math.max(8, Math.round(Number(t.fontSize) || 24)),
                fontWeight: t.fontWeight || 'normal',
                italic: !!t.italic,
                color: t.color || '#ffffff',
                strokeColor: t.strokeColor || '#000000',
                strokeWidth: Math.max(0, Number(t.strokeWidth) || 0),
                layerOpacity,
            });
        }
    }

    drawOps.sort((a, b) => a.z - b.z);

    // Load all unique image URLs in one batch
    const uniqueUrls = [...new Set(drawOps.filter(op => op.url).map(op => op.url))];
    const loaded     = await Promise.all(uniqueUrls.map(loadImg));
    const imgMap     = new Map(uniqueUrls.map((url, i) => [url, loaded[i]]));

    ctx.save();

    for (const op of drawOps) {
        if (op.type === 'moodlight') {
            ctx.save();
            ctx.globalAlpha = op.opacity;
            ctx.fillStyle   = op.color;
            ctx.fillRect(0, 0, W, H);
            ctx.restore();

        } else if (op.type === 'tile') {
            const img = imgMap.get(op.url);
            if (!img) continue;
            const tileOffX = typeof op.tileOffX === 'number' ? op.tileOffX : 0;
            const startX = tileOffX - op.tileW;
            for (let ty = 0; ty * op.tileH < H + op.tileH; ty++)
                for (let x = startX; x < W + op.tileW; x += op.tileW)
                    ctx.drawImage(img, x, ty * op.tileH, op.tileW, op.tileH);
            if (bgKey === 'grey_grid' && gridTint) {
                ctx.save();
                ctx.globalCompositeOperation = 'screen';
                ctx.fillStyle = `${gridTint}80`;
                ctx.fillRect(0, 0, W, H);
                ctx.restore();
            }

        } else if (op.type === 'centered') {
            const img = imgMap.get(op.url);
            if (img) {
                ctx.save();
                if (op.layerOpacity !== undefined && op.layerOpacity < 100) ctx.globalAlpha *= op.layerOpacity / 100;
                ctx.drawImage(img,
                    Math.round(W / 2 - img.naturalWidth  / 2),
                    Math.round(H / 2 - img.naturalHeight / 2)
                );
                ctx.restore();
            }

        } else if (op.type === 'sprite') {
            let img = imgMap.get(op.url);
            if (img) {
                if (op.outlineEffect?.enabled) {
                    const processed = applyOutlineEffect(img, op.outlineEffect.color || '#000000', op.outlineEffect.tolerance ?? 0);
                    if (processed) img = processed;
                }
                if (op.waterEffect?.enabled) {
                    const processed = applyWaterEffect(
                        img,
                        op.waterEffect.color || '#0a6e78',
                        op.waterEffect.height ?? 20,
                        op.spriteKind || 'habbo',
                        op.waterEffect.curveMode || 'adaptive'
                    );
                    if (processed) img = processed;
                }
                ctx.save();
                if (op.layerOpacity !== undefined && op.layerOpacity < 100) ctx.globalAlpha *= op.layerOpacity / 100;
                if (tileMode) {
                    for (const ox of [-W, 0, W]) {
                        for (const oy of [-H, 0, H]) {
                            drawSpriteOntoCtx(ctx, img, { ...op, x: op.x + ox, y: op.y + oy }, W, H);
                        }
                    }
                } else {
                    drawSpriteOntoCtx(ctx, img, op, W, H);
                }
                ctx.restore();
            }

        } else if (op.type === 'text') {
            const italic = op.italic       ? 'italic' : 'normal';
            const weight = op.fontWeight === 'bold' ? 'bold'   : 'normal';
            const fontFamilyLower = (op.fontFamily || '').toLowerCase();
            const isPixelFont = fontFamilyLower.includes('volter') || fontFamilyLower.includes('jersey');
            const dispScale = op.scale || 1;

            let tw, th, tmp;

            if (isPixelFont) {
                // Render at 1x; ctx.scale(dispScale) + imageSmoothingEnabled=false = pixel-perfect upscale
                const m = getTextBoxMetrics(op);
                tw = m.w; th = m.h;
                tmp = document.createElement('canvas');
                tmp.width  = Math.max(1, Math.ceil(tw));
                tmp.height = Math.max(1, Math.ceil(th));
                const tctx = tmp.getContext('2d');
                tctx.font         = `${italic} ${weight} ${op.fontSize}px ${op.fontFamily}`;
                tctx.textAlign    = 'center';
                tctx.textBaseline = 'middle';
                tctx.fillStyle    = op.color;
                tctx.fillText(op.text, tw / 2, th / 2);
                if (op.strokeWidth > 0) {
                    tctx.globalCompositeOperation = 'destination-over';
                    tctx.lineJoin    = 'round';
                    tctx.lineWidth   = op.strokeWidth * 2;
                    tctx.strokeStyle = op.strokeColor;
                    tctx.strokeText(op.text, tw / 2, th / 2);
                    tctx.globalCompositeOperation = 'source-over';
                }
                // Pixel fonts have hard edges by design — threshold alpha to remove the
                // canvas fillText antialiasing fringe before nearest-neighbour upscaling.
                const pd = tctx.getImageData(0, 0, tmp.width, tmp.height);
                for (let i = 3; i < pd.data.length; i += 4) pd.data[i] = pd.data[i] >= 128 ? 255 : 0;
                tctx.putImageData(pd, 0, 0);
            } else {
                // Vector: render at full display resolution (fontSize * dispScale), draw 1:1 — no ctx.scale needed
                const fakeT = { ...op, fontSize: op.fontSize * dispScale, strokeWidth: op.strokeWidth * dispScale };
                const m = getTextBoxMetrics(fakeT);
                tw = m.w / dispScale; th = m.h / dispScale;
                tmp = document.createElement('canvas');
                tmp.width  = Math.max(1, Math.ceil(m.w));
                tmp.height = Math.max(1, Math.ceil(m.h));
                const tctx = tmp.getContext('2d');
                tctx.font         = `${italic} ${weight} ${op.fontSize * dispScale}px ${op.fontFamily}`;
                tctx.textAlign    = 'center';
                tctx.textBaseline = 'middle';
                tctx.fillStyle    = op.color;
                tctx.fillText(op.text, m.w / 2, m.h / 2);
                if (op.strokeWidth > 0) {
                    tctx.globalCompositeOperation = 'destination-over';
                    tctx.lineJoin    = 'round';
                    tctx.lineWidth   = op.strokeWidth * dispScale * 2;
                    tctx.strokeStyle = op.strokeColor;
                    tctx.strokeText(op.text, m.w / 2, m.h / 2);
                    tctx.globalCompositeOperation = 'source-over';
                }
            }

            const baseCx = Math.round(W / 2 + op.x);
            const baseCy = Math.round(H / 2 + op.y);
            const offsets = tileMode ? [[-W, -H], [-W, 0], [-W, H], [0, -H], [0, 0], [0, H], [W, -H], [W, 0], [W, H]] : [[0, 0]];
            for (const [ox, oy] of offsets) {
                ctx.save();
                if (op.layerOpacity !== undefined && op.layerOpacity < 100) ctx.globalAlpha *= op.layerOpacity / 100;
                ctx.translate(baseCx + ox, baseCy + oy);
                if (op.rotation) ctx.rotate(op.rotation * Math.PI / 180);
                if (isPixelFont) {
                    if (dispScale !== 1) ctx.scale(dispScale, dispScale);
                    if (op.flipX) ctx.scale(-1,  1);
                    if (op.flipY) ctx.scale( 1, -1);
                    ctx.imageSmoothingEnabled = false;
                    ctx.drawImage(tmp, Math.round(-tw / 2), Math.round(-th / 2), tw, th);
                } else {
                    if (op.flipX) ctx.scale(-1,  1);
                    if (op.flipY) ctx.scale( 1, -1);
                    ctx.imageSmoothingEnabled = true;
                    ctx.imageSmoothingQuality = 'high';
                    ctx.drawImage(tmp, Math.round(-tw / 2), Math.round(-th / 2), tw, th);
                }
                ctx.restore();
            }
            tmp.width = 0; // free memory
        }
    }

    ctx.restore();

    // ─── Speech bubbles ───────────────────────────────────────────────────────
    if (speechParts) {
        const bubbles = Array.isArray(scene?.speechBubbles) ? scene.speechBubbles.slice(0, 5) : [];
        if (bubbles.length) {
            const scale  = 0.5;
            const rowH   = Math.round(40 * scale);
            const gapY   = 2;
            const leftW  = Math.round(33 * scale);
            const rightW = Math.round(33 * scale);
            const padX   = Math.max(4, Math.round(10 * scale));
            const maxMidW = Math.max(20, Math.floor(W - 10 - (leftW + rightW)));

            // Position bubbles just above the topmost Habbo sprite
            const sceneSprites = Array.isArray(scene?.sceneSprites) ? scene.sceneSprites : [];
            let topHabboY = null;
            sceneSprites.forEach(sp => {
                const cy = H / 2 + (cameraPos.y || 0) + (sp.pos?.y || 0);
                if (topHabboY === null || cy - 50 < topHabboY) topHabboY = cy - 50;
            });
            const stackOffset = Math.max(0, bubbles.length - 1) * (rowH + gapY);
            const minBaseY    = H * 0.2 + stackOffset;
            const baseY = topHabboY !== null
                ? Math.max(minBaseY, Math.round(topHabboY) - 35)
                : Math.max(minBaseY, 120);

            const uniqSrcs = [...new Set(bubbles.flatMap(b => [
                b.assets?.frnt || speechParts.frnt,
                b.assets?.mid  || speechParts.mid,
                b.assets?.end  || speechParts.end,
            ]))];
            const loadedBubble = await Promise.all(uniqSrcs.map(loadImg));
            const bubbleMap = new Map(uniqSrcs.map((src, i) => [src, loadedBubble[i]]));

            const regFont  = '9px VolterGoldfish, sans-serif';
            const boldFont = '9px VolterBoldGoldfish, VolterGoldfish, sans-serif';
            const meas = (s, font) => {
                if (!_measureCtx) _measureCtx = document.createElement('canvas').getContext('2d');
                _measureCtx.font = font;
                return Math.ceil(_measureCtx.measureText(String(s || '')).width || 0);
            };
            const trunc = (s, maxPx, font) => {
                const str = String(s || '');
                if (meas(str, font) <= maxPx) return str;
                const ell = '…';
                const ellW = meas(ell, font);
                let lo = 0, hi = str.length;
                while (lo < hi) {
                    const mid2 = Math.ceil((lo + hi) / 2);
                    if (meas(str.slice(0, mid2), font) + ellW <= maxPx) lo = mid2; else hi = mid2 - 1;
                }
                return str.slice(0, lo) + ell;
            };

            for (let i = 0; i < bubbles.length; i++) {
                const b  = bubbles[i];
                const sp = sceneSprites.find(x => x.id === b.sceneHabboId) || null;
                const username = String(
                    (b.displayName && b.displayName !== 'Habbo') ? b.displayName : (sp?.snap?.habboName || 'Habbo')
                ).trim();
                const msgRaw  = String(b.text || '').replace(/\s+/g, ' ').trim();
                const namePart = `${username}: `;
                const nameW   = meas(namePart, boldFont);
                const msgFont = b.shout ? boldFont : regFont;
                const msg     = trunc(msgRaw, Math.max(0, maxMidW - padX * 2 - nameW), msgFont);
                const textW   = nameW + meas(msg, msgFont);
                const midW    = Math.min(maxMidW, Math.max(20, textW + padX * 2 + Math.round(14 * scale)));
                const totalW  = leftW + midW + rightW;
                const xCenter = Math.round(W / 2 + (cameraPos.x || 0) + (sp?.pos?.x || 0));
                let left2 = Math.round(xCenter - totalW / 2);
                if (left2 < 5) left2 = 5;
                if (left2 > W - 5 - totalW) left2 = Math.round(W - 5 - totalW);
                const y = baseY - (bubbles.length - 1 - i) * (rowH + gapY);

                const frntIm = bubbleMap.get(b.assets?.frnt || speechParts.frnt);
                const midIm  = bubbleMap.get(b.assets?.mid  || speechParts.mid);
                const endIm  = bubbleMap.get(b.assets?.end  || speechParts.end);

                if (frntIm) ctx.drawImage(frntIm, left2, y, leftW, rowH);
                if (midIm) {
                    const bTileW = Math.max(1, Math.round((midIm.naturalWidth || 4) * scale));
                    for (let xx = 0; xx < midW; xx += bTileW) {
                        const dw = Math.min(bTileW, midW - xx);
                        ctx.drawImage(midIm, 0, 0,
                            Math.min(midIm.naturalWidth || bTileW, dw / scale), midIm.naturalHeight || 40,
                            left2 + leftW + xx, y, dw, rowH);
                    }
                }
                if (endIm) ctx.drawImage(endIm, left2 + leftW + midW, y, rightW, rowH);

                // Render Volter text via tmp canvas + nearest-neighbour drawImage for pixel-perfect output
                const textY = y + Math.round(rowH / 2) + 1;
                const drawBubbleText = (str, font, measW, destX) => {
                    if (!str) return;
                    const bTmp = document.createElement('canvas');
                    bTmp.width  = Math.max(1, measW + 4);
                    bTmp.height = Math.max(1, rowH + 4);
                    const btctx = bTmp.getContext('2d');
                    btctx.font         = font;
                    btctx.textBaseline = 'middle';
                    btctx.fillStyle    = '#000';
                    btctx.fillText(str, 0, bTmp.height / 2 + 1);
                    // Volter is a pixel font — threshold alpha to remove antialiasing fringe.
                    const bpd = btctx.getImageData(0, 0, bTmp.width, bTmp.height);
                    for (let qi = 3; qi < bpd.data.length; qi += 4) bpd.data[qi] = bpd.data[qi] >= 128 ? 255 : 0;
                    btctx.putImageData(bpd, 0, 0);
                    ctx.imageSmoothingEnabled = false;
                    ctx.drawImage(bTmp, destX, y - 2, bTmp.width, bTmp.height);
                };
                drawBubbleText(namePart, boldFont, nameW, left2 + leftW + padX);
                drawBubbleText(msg, msgFont, meas(msg, msgFont), left2 + leftW + padX + nameW);
            }
        }
    }

    // ─── Image border ─────────────────────────────────────────────────────────
    if (scene?.imageBorderEnabled) {
        const bColor = /^#[0-9a-fA-F]{6}$/.test(String(scene.imageBorderColor || '').trim())
            ? scene.imageBorderColor.trim() : '#ffffff';
        const bWidth = Math.max(1, Math.min(100, Math.round(Number(scene.imageBorderWidth) || 10)));
        ctx.fillStyle = bColor;
        ctx.fillRect(0, 0, W, bWidth);            // top
        ctx.fillRect(0, H - bWidth, W, bWidth);   // bottom
        ctx.fillRect(0, 0, bWidth, H);            // left
        ctx.fillRect(W - bWidth, 0, bWidth, H);   // right
    }

    return canvas;
}